import React from "react";
import {
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Box,
  CardMedia,
  Link,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const BRAND = "#f05d32";

const MyDialogTitle = (props) => {
  const { children, onClose, ...other } = props;
  return (
    <DialogTitle
      sx={{
        m: 0,
        px: 3,
        py: 2,
        backgroundColor: BRAND,
        color: "#fff",
        display: "flex",
        alignItems: "center",
        position: "relative",
        fontWeight: 600,
        fontSize: { xs: 18, sm: 20 },
        letterSpacing: "0.01em",
      }}
      {...other}
    >
      {children}
      {onClose ? (
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: "50%",
            transform: "translate(0,-50%)",
            color: "#fff",
            "&:hover": {
              bgcolor: "rgba(255,255,255,0.15)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </DialogTitle>
  );
};

const SectionLabel = ({ children }) => (
  <Typography
    align="center"
    sx={{
      fontSize: 13,
      fontWeight: 600,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: "#8a8a8a",
      mb: 1.75,
    }}
  >
    {children}
  </Typography>
);

const OptionCard = ({ image, alt, label, onClick, href }) => {
  const content = (
    <>
      <Box
        sx={{
          width: 72,
          height: 72,
          borderRadius: "50%",
          bgcolor: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 1,
          boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
          overflow: "hidden",
        }}
      >
        <CardMedia
          sx={{ width: "78%", margin: "0 auto" }}
          component="img"
          image={image}
          alt={alt}
        />
      </Box>
      <Typography
        align="center"
        sx={{
          fontSize: { xs: 13, sm: 14 },
          lineHeight: 1.2,
          fontWeight: 600,
          color: "#1a1a1a",
        }}
      >
        {label}
      </Typography>
    </>
  );

  const sx = {
    textDecoration: "none",
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    flex: "1 1 0",
    minWidth: 88,
    maxWidth: 110,
    p: 1.25,
    borderRadius: "16px",
    border: "1px solid rgba(0,0,0,0.06)",
    bgcolor: "#fff",
    transition:
      "border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease",
    "&:hover": {
      borderColor: "rgba(240, 93, 50, 0.4)",
      boxShadow: "0 6px 18px rgba(240, 93, 50, 0.1)",
      transform: "translateY(-2px)",
    },
  };

  if (href) {
    return (
      <Link href={href} target="_blank" rel="noreferrer" sx={sx}>
        {content}
      </Link>
    );
  }

  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        ...sx,
        border: "1px solid rgba(0,0,0,0.06)",
        background: "#fff",
        font: "inherit",
      }}
    >
      {content}
    </Box>
  );
};

export default function DeliveryType(props) {
  const { component } = props;
  const filesUrl = component.getHelpers("env").get("files_url");
  const img_version = component.getHelpers("value").getValue("images-version");

  const open = component.getData("default.open", false);

  const apps = [
    {
      slug: "wolt",
      name: "Wolt",
      url: "https://wolt.com/el/grc/athens/restaurant/il-palatino?srsltid=AfmBOoqHy1bw53ScXCotW2lnJIlcbm-7hlWuJsY184CfanOzav6dBg0c",
    },
    {
      slug: "box",
      name: "Box",
      url: "https://box.gr/delivery/goudi/il-palatino-goudi",
    },
    {
      slug: "efood",
      name: "efood",
      url: "https://www.e-food.gr/delivery/menu/il-palatino",
    },
  ];

  return (
    <Dialog
      onClose={() => {
        component.setData({ "default.open": false });
      }}
      open={open}
      sx={{
        "& .MuiPaper-root": {
          borderRadius: { xs: "20px", sm: "20px" },
          minWidth: { xs: "92%", sm: 460 },
          maxWidth: { sm: 500 },
          overflow: "hidden",
        },
      }}
    >
      <MyDialogTitle
        onClose={() => {
          component.setData({ "default.open": false });
        }}
      >
        {component.ucfirst("delivery-type-dialog-title")}
      </MyDialogTitle>

      <DialogContent
        dividers
        sx={{
          p: { xs: 2.5, sm: 3 },
          bgcolor: "#fafafa",
          borderColor: "rgba(0,0,0,0.06)",
        }}
      >
        <Box sx={{ mb: 3 }}>
          <SectionLabel>{component.ucfirst("takeaway-title")}</SectionLabel>
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <OptionCard
              image={`${filesUrl}/assets/images/takeaway.png?v=${img_version}`}
              alt="takeaway image"
              label={component.ucfirst("footer-title-1")}
              onClick={() => component.updateDeliveryType("takeaway")}
            />
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            my: 0.5,
            mb: 3,
          }}
        >
          <Box
            sx={{
              flex: 1,
              height: "1px",
              background:
                "linear-gradient(90deg, transparent, rgba(240,93,50,0.35))",
            }}
          />
          <Box
            sx={{
              px: 1.75,
              py: 0.5,
              borderRadius: "50px",
              bgcolor: "rgba(240, 93, 50, 0.1)",
              border: "1px solid rgba(240, 93, 50, 0.25)",
              color: BRAND,
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              lineHeight: 1,
              flexShrink: 0,
            }}
          >
            ή
          </Box>
          <Box
            sx={{
              flex: 1,
              height: "1px",
              background:
                "linear-gradient(90deg, rgba(240,93,50,0.35), transparent)",
            }}
          />
        </Box>

        <Box>
          <SectionLabel>{component.ucfirst("delivery-title")}</SectionLabel>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              flexWrap: "wrap",
              gap: 1.25,
            }}
          >
            {apps.map((app) => {
              const { slug, name, url } = app;
              return (
                <OptionCard
                  key={slug}
                  image={`${filesUrl}/assets/images/${slug}.png?v=${img_version}`}
                  alt={`${name} image`}
                  label={component.ucfirst(name)}
                  href={url}
                />
              );
            })}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
